/* BatBaby cart drawer, backed by Shopify's real cart (Cart AJAX API).
   Every number shown comes from Shopify, so the drawer always agrees with
   checkout. Never reintroduce hardcoded prices.

   Three Shopify behaviours this file exists to absorb. All three were found
   by testing against the live cart, so please do not "simplify" them away:

   1. A Buy X Get Y discount makes Shopify SPLIT one variant across several
      lines (some discounted, some not). fhGroupItems() merges them back so
      the customer sees one row per size.

   2. Quantity changes MUST go through /cart/update.js with a positional
      array covering every line. /cart/change.js and /cart/update.js keyed
      by variant id both apply the quantity to EVERY line of that variant,
      so asking for 3 silently gives you 6. See fhSetVariantQty().

   3. Discounts arrive in three different shapes depending on discount type
      and endpoint:
        /cart.js cart-level -> { title, total_allocated_amount }
        /cart/change.js     -> { amount, discount_application: { title } }
        Buy X Get Y         -> items[].line_level_discount_allocations
      fhDiscountLines() normalises and sums all of them by title. Reading
      .total_allocated_amount off the wrong shape yields NaN. */

var FH_MONEY = function (cents) { return '$' + (Number(cents) / 100).toFixed(2); };

function fhEscape(str) {
  return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
    return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
  });
}

function fhCartRequest(url, options) {
  var opts = options || {};
  opts.headers = Object.assign({ 'Content-Type': 'application/json', 'Accept': 'application/json' }, opts.headers || {});
  return fetch(url, opts).then(function (res) {
    if (!res.ok) {
      return res.json().then(function (e) { throw new Error(e.description || 'Cart request failed'); });
    }
    return res.json();
  });
}

/* Always cache-busted: a stale /cart.js shows the customer the wrong total. */
function fhGetCart() {
  return fhCartRequest('/cart.js?_=' + Date.now(), { cache: 'no-store' });
}

function fhGroupItems(cart) {
  var map = {}, order = [];
  (cart.items || []).forEach(function (it) {
    var key = String(it.variant_id);
    if (!map[key]) {
      map[key] = {
        variant_id: it.variant_id,
        product_title: it.product_title,
        variant_title: it.variant_title,
        image: it.image,
        quantity: 0,
        original_line_price: 0
      };
      order.push(key);
    }
    map[key].quantity += it.quantity;
    map[key].original_line_price += it.original_line_price;
  });
  return order.map(function (k) { return map[k]; });
}

function fhDiscountLines(cart) {
  var totals = {}, order = [];

  function add(title, amount) {
    amount = Number(amount);
    if (!isFinite(amount) || amount <= 0) return;
    title = title || 'Discount';
    if (!(title in totals)) { totals[title] = 0; order.push(title); }
    totals[title] += amount;
  }

  (cart.cart_level_discount_applications || []).forEach(function (entry) {
    var app = entry.discount_application || entry;
    add(app.title, entry.amount != null ? entry.amount : app.total_allocated_amount);
  });

  (cart.items || []).forEach(function (it) {
    var allocs = it.line_level_discount_allocations || it.discounts || [];
    allocs.forEach(function (a) {
      var app = a.discount_application || a;
      add(app.title || a.title, a.amount);
    });
  });

  return order.map(function (t) { return { title: t, amount: totals[t] }; });
}

function fhOriginalSubtotal(cart) {
  return (cart.items || []).reduce(function (sum, it) { return sum + it.original_line_price; }, 0);
}

function fhRefreshCart(shouldOpen) {
  return fhGetCart().then(function (cart) {
    fhRenderCart(cart);
    if (shouldOpen) openCart();
  }).catch(function (err) {
    console.error('[BatBaby] cart refresh failed:', err);
  });
}

function addToCart(quantity) {
  var variantId = window.fhSelectedVariantId;
  if (!variantId) {
    var err = document.getElementById('fhSizeError');
    if (err) {
      err.classList.remove('hidden');
      err.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      alert('Please choose a size first.');
    }
    return;
  }
  var buttons = document.querySelectorAll('[data-add-to-cart]');
  buttons.forEach(function (b) { b.disabled = true; b.style.opacity = '0.6'; });

  return fhCartRequest('/cart/add.js', {
    method: 'POST',
    body: JSON.stringify({ items: [{ id: Number(variantId), quantity: quantity || 1 }] })
  }).then(function () {
    return fhRefreshCart(true);
  }).catch(function (err) {
    console.error('[BatBaby] add to cart failed:', err);
    alert('Sorry, that could not be added to your cart. Please try again.');
  }).then(function () {
    buttons.forEach(function (b) { b.disabled = false; b.style.opacity = ''; });
  });
}

/* Sets the TOTAL quantity of one variant. Reads the cart, builds a positional
   quantity array for every line, puts the new quantity on the first line of
   this variant and 0 on its other lines, then sends one atomic update. */
function fhSetVariantQty(variantId, quantity) {
  var wanted = Math.max(0, quantity);
  return fhGetCart().then(function (cart) {
    var placed = false;
    var updates = (cart.items || []).map(function (it) {
      if (String(it.variant_id) !== String(variantId)) return it.quantity;
      if (!placed) { placed = true; return wanted; }
      return 0;
    });
    return fhCartRequest('/cart/update.js', {
      method: 'POST',
      body: JSON.stringify({ updates: updates })
    });
  }).then(function () {
    return fhRefreshCart(false);
  }).catch(function (err) {
    console.error('[BatBaby] quantity update failed:', err);
  });
}

function fhRenderCart(cart) {
  var countEl = document.getElementById('cartCount');
  if (countEl) {
    countEl.textContent = cart.item_count;
    countEl.style.transform = cart.item_count > 0 ? 'scale(1)' : 'scale(0)';
  }

  var body = document.getElementById('cartBody');
  var foot = document.getElementById('cartFoot');
  if (!body || !foot) return;

  if (!cart.item_count) {
    foot.classList.add('hidden');
    body.innerHTML = '<div class="h-full grid place-items-center text-center text-ink/50">'
      + '<div><div class="mx-auto mb-3 grid place-items-center h-14 w-14 rounded-full bg-mist">'
      + '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#7E22CE" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="20" r="1.4"/><circle cx="18" cy="20" r="1.4"/><path d="M2 3h2.2l1.9 12.2a1.6 1.6 0 0 0 1.6 1.3h8.9a1.6 1.6 0 0 0 1.6-1.3L21 7H5.5"/></svg>'
      + '</div>Your cart is empty.<div class="mt-4"><button onclick="closeCart()" class="rounded-full bg-forest text-paper px-5 py-2.5 text-sm font-semibold">Start shopping</button></div></div></div>';
    return;
  }

  foot.classList.remove('hidden');

  body.innerHTML = fhGroupItems(cart).map(function (item, i) {
    var img = item.image ? item.image.replace(/(\.[a-zA-Z0-9]+)(\?.*)?$/, '_200x$1$2') : '';
    return '<div class="flex gap-4 rounded-3xl bg-mist p-4 ' + (i > 0 ? 'mt-3' : '') + '">'
      + (img ? '<img src="' + fhEscape(img) + '" alt="' + fhEscape(item.product_title) + '" class="h-24 w-24 object-contain rounded-2xl bg-paper" />' : '')
      + '<div class="flex-1">'
      + '<div class="font-semibold">' + fhEscape(item.product_title) + '</div>'
      + (item.variant_title ? '<div class="mt-0.5 text-[13px] text-ink/50">' + fhEscape(item.variant_title) + '</div>' : '')
      + '<div class="mt-3 flex items-center justify-between">'
      + '<div class="inline-flex items-center rounded-full border border-ink/15 bg-paper">'
      + '<button onclick="fhSetVariantQty(' + item.variant_id + ',' + (item.quantity - 1) + ')" class="h-8 w-8 grid place-items-center text-lg" aria-label="Decrease quantity">&minus;</button>'
      + '<span class="w-6 text-center text-sm font-semibold">' + item.quantity + '</span>'
      + '<button onclick="fhSetVariantQty(' + item.variant_id + ',' + (item.quantity + 1) + ')" class="h-8 w-8 grid place-items-center text-lg" aria-label="Increase quantity">+</button>'
      + '</div>'
      + '<div class="font-display font-bold">' + FH_MONEY(item.original_line_price) + '</div>'
      + '</div></div></div>';
  }).join('');

  var discounts = fhDiscountLines(cart).map(function (d) {
    return '<div class="flex items-center justify-between text-sm font-semibold text-moss">'
      + '<span>' + fhEscape(d.title) + '</span>'
      + '<span>&minus; ' + FH_MONEY(d.amount) + '</span></div>';
  }).join('');

  var discountsEl = document.getElementById('cartDiscounts');
  if (discountsEl) discountsEl.innerHTML = discounts;

  var subtotalEl = document.getElementById('cartSubtotal');
  if (subtotalEl) subtotalEl.textContent = FH_MONEY(fhOriginalSubtotal(cart));

  var totalEl = document.getElementById('cartTotal');
  if (totalEl) totalEl.textContent = FH_MONEY(cart.total_price);
}

function checkout() { window.location.href = '/checkout'; }

document.addEventListener('DOMContentLoaded', function () { fhRefreshCart(false); });
